import * as THREE from 'three';
import { std, stone } from '../materials.ts';
import { M, V, eyesGlow, limb } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/**
 * La Picotera (docs/sendas/artifice.md, chapter 2, act 3): the great gargoyle that sits on the winch's drum. A gargoyle's
 * frame (its bones the same, so it moves as one), but heavy: a hunched body of dark weathered stone split by pale
 * lichen, a long hooked beak like a quarryman's pick (iron-dark, its point worn bright), a crest of broken horns, wide
 * ragged wings and red eyes. It reads as the boss from far: bigger, darker, the beak.
 */
export const picotera = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 34);
  const { g } = c;
  const st = stone('#45444a');
  const pale = stone('#6f7670');
  const beakM = std('#2a2d33', { roughness: 0.4, metalness: 0.4 });
  const tip = std('#c9ced6', { roughness: 0.3, metalness: 0.6 });
  const perch = joint(g, V(0, 0.175, 0), 'perch');
  M(new THREE.BoxGeometry(0.01, 0.01, 0.01), st, perch.abs);
  const body = joint(g, V(0, 0.5, -0.02), 'body');
  const head = joint(body.abs, V(0, 0.88, 0.1), 'head');
  const bodyM = M(new THREE.SphereGeometry(0.26, 12, 9), st, body.abs);
  bodyM.scale.set(1.15, 1.15, 1.0);
  bodyM.position.set(0, 0.66, 0);
  bodyM.rotation.x = 0.62;
  // A hump of lichened stone on its back.
  const hump = M(new THREE.DodecahedronGeometry(0.16, 0), pale, body.abs);
  hump.position.set(0, 0.84, -0.12);
  const skull = M(new THREE.BoxGeometry(0.2, 0.16, 0.22), st, head.abs);
  skull.position.set(0, 0.95, 0.15);
  // The beak: a long hooked pick, its point bright.
  const beak = M(new THREE.ConeGeometry(0.05, 0.36, 6), beakM, head.abs);
  beak.position.set(0, 0.9, 0.38);
  beak.rotation.x = Math.PI / 2 + 0.35;
  const point = M(new THREE.ConeGeometry(0.02, 0.08, 5), tip, head.abs);
  point.position.set(0, 0.83, 0.55);
  point.rotation.x = Math.PI / 2 + 0.9;
  const skel: Record<string, THREE.Object3D> = { perch: perch.p, body: body.p, head: head.p };
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    for (let h = 0; h < 3; h++) {
      const horn = M(new THREE.ConeGeometry(0.025, 0.14 + h * 0.03, 5), pale, head.abs);
      horn.position.set(k * (0.05 + h * 0.03), 1.05 - h * 0.02, 0.08 - h * 0.05);
      horn.rotation.set(-0.8 - h * 0.2, 0, -k * (0.3 + h * 0.15));
    }
    const arm = joint(body.abs, V(k * 0.17, 0.74, 0.12), `arm${s}`);
    const leg = joint(body.abs, V(k * 0.14, 0.5, -0.05), `leg${s}`);
    const wj = joint(body.abs, V(k * 0.13, 0.82, -0.14), `wing${s}`);
    limb(arm.abs, V(k * 0.17, 0.74, 0.12), V(k * 0.22, 0.4, 0.24), 0.05, 0.06, st, 6);
    limb(leg.abs, V(k * 0.14, 0.5, -0.05), V(k * 0.18, 0.36, 0.14), 0.07, 0.08, st, 6);
    // Talons.
    for (let t = -1; t <= 1; t++) {
      const cl = M(new THREE.ConeGeometry(0.015, 0.08, 4), beakM, leg.abs);
      cl.position.set(k * 0.18 + t * 0.03, 0.34, 0.2);
      cl.rotation.x = Math.PI / 2;
    }
    const w = new THREE.Shape();
    w.moveTo(0, 0);
    w.lineTo(0.75, 0.48);
    w.lineTo(0.7, 0.2);
    w.lineTo(0.6, 0.24);
    w.lineTo(0.55, 0.02);
    w.lineTo(0.42, 0.06);
    w.lineTo(0.36, -0.12);
    w.lineTo(0.22, -0.06);
    w.lineTo(0.14, -0.2);
    w.closePath();
    const wing = M(new THREE.ExtrudeGeometry(w, { depth: 0.025, bevelEnabled: false }), st, wj.abs);
    wing.position.set(k * 0.13, 0.82, -0.14);
    wing.scale.x = k;
    wing.rotation.y = k * 0.45;
    Object.assign(skel, { [`arm${s}`]: arm.p, [`leg${s}`]: leg.p, [`wing${s}`]: wj.p });
  }
  eyesGlow(head.abs, '#ff4f3a', 0.98, 0.27, 0.06);
  return done(c, 1.15, undefined, undefined, bonesOf(c, skel));
};
